import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import i18n from '../../i18n/config.js';
import ProjectBreakdownDonut from './ProjectBreakdownDonut';
import ContractProgressBar from './ContractProgressBar';
import ProjectStatusHeader from './ProjectStatusHeader';
import './ProjectDetailModal.css';
import '../Overview/CashflowChart.css';

/**
 * Round hours to the nearest half hour for display (e.g. 1.24 -> 1.0, 1.26 -> 1.5).
 * @param {number} value
 * @returns {number}
 */
const roundHoursToHalf = (value) => Math.round(Number(value) * 2) / 2;

const PERFORMANCE_COLOR = {
  on_track: 'var(--color-lamp-on-track)',
  follow_up: 'var(--color-lamp-follow-up)',
  action_required: 'var(--color-lamp-action-required)',
  information: 'var(--color-text-tertiary)',
};

const WARNING_CATEGORY_RANK = {
  action_required: 0,
  follow_up: 1,
  information: 2,
};

const WARNING_TYPE_RANK = {
  overdue: 0,
  profitability: 1,
  uninvoiced: 2,
};

const COST_METRICS = [
  'personnel_costs',
  'material_costs',
  'subcontractor_costs',
  'other_costs',
];

/**
 * Sum the values that are present. Null stays null when every value is missing.
 * @param {Array<number|null|undefined>} values
 * @returns {number|null}
 */
const sumPresent = (values) => {
  const present = values.filter((value) => value !== null && value !== undefined);
  if (present.length === 0) return null;
  return present.reduce((sum, value) => sum + Number(value), 0);
};

const PERFORMANCE_SENTENCE = {
  on_track: 'projects.details.performanceOnTrack',
  follow_up: 'projects.details.performanceFollowUp',
  action_required: 'projects.details.performanceActionRequired',
};

/**
 * Compare family rows by Tripletex number, then name.
 * @param {Object} left
 * @param {Object} right
 * @returns {number}
 */
const compareFamilyMembers = (left, right) => {
  const numberDelta = (left.source_system_project_number ?? '').localeCompare(
    right.source_system_project_number ?? '',
    undefined,
    { numeric: true }
  );
  if (numberDelta !== 0) return numberDelta;
  return (left.project_name ?? '').localeCompare(right.project_name ?? '');
};

/**
 * Flatten a reporting family into indented rows. A missing parent is top level.
 * @param {Array<Object>} members
 * @returns {Array<{ member: Object, depth: number }>}
 */
const familyRows = (members) => {
  const ids = new Set(members.map((member) => member.id));
  const byParent = new Map();
  members.forEach((member) => {
    const parentId = member.parent_project_id;
    const key = parentId != null && ids.has(parentId) ? parentId : null;
    const list = byParent.get(key) ?? [];
    list.push(member);
    byParent.set(key, list);
  });

  const rows = [];
  const walk = (parentId, depth, seen) => {
    const children = (byParent.get(parentId) ?? []).slice().sort(compareFamilyMembers);
    children.forEach((member) => {
      if (seen.has(member.id)) return;
      seen.add(member.id);
      rows.push({ member, depth });
      walk(member.id, depth + 1, seen);
    });
  };
  walk(null, 0, new Set());
  return rows;
};

/**
 * Project detail body reused on the project page.
 * @param {Object} props
 * @param {Object} props.project
 * @param {Object|null} [props.warnings] - Warnings payload, or null until it has loaded
 * @param {number[]|null} [props.selectedProjectIds] - Included booking projects. Null is the whole family.
 * @param {Function} [props.onSelectFamily] - Select the whole family
 * @param {Function} [props.onToggleMember] - Toggle one booking project
 * @returns {JSX.Element}
 */
const ProjectDetailContent = ({
  project,
  warnings = null,
  selectedProjectIds = null,
  onSelectFamily,
  onToggleMember,
}) => {
  const { t } = useTranslation();
  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';
  const [includeNonInvoicedWork, setIncludeNonInvoicedWork] = useState(true);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatHours = (value) => new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);

  const formatDecimalAsPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${(Number(value) * 100).toFixed(1)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    } catch {
      return '-';
    }
  };

  const revenueSlices = useMemo(() => {
    if (!project) return [];
    const slices = [
      { id: 'hours', label: t('projects.details.revenueHours'), value: Number(project.revenue_hours) || 0 },
      { id: 'fixed', label: t('projects.details.revenueFixedPrice'), value: Number(project.revenue_fixed_price) || 0 },
      { id: 'material', label: t('projects.details.revenueMaterial'), value: Number(project.revenue_material) || 0 },
      { id: 'other', label: t('projects.details.revenueOther'), value: Number(project.revenue_other) || 0 },
    ];
    if (includeNonInvoicedWork) {
      slices.push({
        id: 'non_invoiced_work',
        label: t('projects.details.nonInvoicedWork'),
        value: Number(project.non_invoiced_work) || 0,
      });
    }
    return slices;
  }, [project, t, includeNonInvoicedWork]);

  const costSlices = useMemo(() => {
    if (!project) return [];
    return [
      { id: 'personnel', label: t('projects.details.personnelCost'), value: Number(project.personnel_costs) || 0 },
      { id: 'material', label: t('projects.details.materialCosts'), value: Number(project.material_costs) || 0 },
      { id: 'subcontractor', label: t('projects.details.subcontractorCosts'), value: Number(project.subcontractor_costs) || 0 },
      { id: 'logistics', label: t('projects.details.logisticsCosts'), value: Number(project.logistics_costs) || 0 },
      { id: 'other', label: t('projects.columns.otherCosts'), value: Number(project.other_costs) || 0 },
    ];
  }, [project, t]);

  const members = project?.members || [];
  const indentedMembers = useMemo(
    () => familyRows(project?.members || []),
    [project?.members]
  );

  const hoursSlices = useMemo(() => {
    if (!project) return [];
    return (project.hours_by_activity || []).map((row) => {
      const activityName = row.activity_name;
      return {
        id: activityName || 'unknown',
        label: activityName || t('projects.details.hoursUnknownActivity'),
        value: roundHoursToHalf(Number(row.hours) || 0),
      };
    });
  }, [project, t]);

  const plannedVsActual = project.planned_vs_actual || {};
  const hasBudgetLines = [
    'hours',
    'personnel_costs',
    'material_costs',
    'subcontractor_costs',
    'other_costs',
  ].some((key) => plannedVsActual[key]?.planned != null);

  const formatHoursValue = (value) => {
    if (value === null || value === undefined) return '-';
    return Number(value).toFixed(1);
  };

  const formatPercentUsed = (metric) => {
    if (!metric || metric.percent_used === null || metric.percent_used === undefined) {
      return '';
    }
    return formatDecimalAsPercent(metric.percent_used);
  };

  const performanceCategory = warnings?.project_performance_category;
  const performanceSentenceKey = PERFORMANCE_SENTENCE[performanceCategory];
  const statusColor = warnings ? (PERFORMANCE_COLOR[performanceCategory] || null) : null;
  const statusSummary = performanceSentenceKey ? t(performanceSentenceKey) : null;

  const warningLines = useMemo(() => {
    if (!warnings) return [];

    const lines = [];
    const overdueInvoices = warnings.overdue_invoices || [];
    if (overdueInvoices.length === 1) {
      const invoice = overdueInvoices[0];
      lines.push({
        category: invoice.category,
        type: 'overdue',
        index: 0,
        text: t('projects.details.overdueInvoiceWarning', {
          invoiceNumber: invoice.invoice_number ?? '-',
          daysOverdue: invoice.days_overdue,
          amount: formatAmount(invoice.remaining_amount),
        }),
      });
    } else if (overdueInvoices.length > 1) {
      const totalRemaining = overdueInvoices.reduce(
        (sum, invoice) => sum + (Number(invoice.remaining_amount) || 0),
        0
      );
      const worstDaysOverdue = overdueInvoices.reduce(
        (maxDays, invoice) => Math.max(maxDays, Number(invoice.days_overdue) || 0),
        0
      );
      let worstCategory = 'information';
      if (overdueInvoices.some((invoice) => invoice.category === 'action_required')) {
        worstCategory = 'action_required';
      } else if (overdueInvoices.some((invoice) => invoice.category === 'follow_up')) {
        worstCategory = 'follow_up';
      }
      lines.push({
        category: worstCategory,
        type: 'overdue',
        index: 0,
        text: t('projects.details.overdueInvoicesSummary', {
          count: overdueInvoices.length,
          daysOverdue: worstDaysOverdue,
          amount: formatAmount(totalRemaining),
        }),
      });
    }

    const profitability = warnings.profitability;
    if (profitability) {
      const margin = formatDecimalAsPercent(profitability.margin_with_non_invoiced_work);
      const benchmark = formatDecimalAsPercent(profitability.benchmark_margin);
      let text = null;
      if (profitability.category === 'action_required') {
        text = t('projects.details.profitabilityAtOrBelowZero', { margin });
      } else if (profitability.benchmark_source === 'budget') {
        text = t('projects.details.profitabilityBelowBudget', { margin, benchmark });
      } else if (profitability.benchmark_source === 'company_average') {
        text = t('projects.details.profitabilityBelowCompanyAverage', { margin, benchmark });
      }
      if (text) {
        lines.push({
          category: profitability.category,
          type: 'profitability',
          index: 0,
          text,
        });
      }
    }

    const uninvoiced = warnings.uninvoiced_work;
    if (uninvoiced) {
      const hours = Number(uninvoiced.hours_after_last_invoice);
      const costs = Number(uninvoiced.costs_after_last_invoice);
      const hasHours = Number.isFinite(hours) && hours !== 0;
      const hasCosts = Number.isFinite(costs) && Math.round(costs) !== 0;
      const days = uninvoiced.days_since_last_invoice;
      let text;
      if (days == null) {
        text = t('projects.details.uninvoicedWorkWarning', {
          days: uninvoiced.days_after_expected,
          expectedDate: formatDate(uninvoiced.expected_invoice_date),
          lastInvoiceDate: formatDate(uninvoiced.last_invoice_date),
        });
      } else if (hasHours && hasCosts) {
        text = t('projects.details.uninvoicedWorkHoursAndCosts', {
          days,
          hours: formatHours(hours),
          amount: formatAmount(costs),
        });
      } else if (hasHours) {
        text = t('projects.details.uninvoicedWorkHours', {
          days,
          hours: formatHours(hours),
        });
      } else if (hasCosts) {
        text = t('projects.details.uninvoicedWorkCosts', {
          days,
          amount: formatAmount(costs),
        });
      } else {
        text = t('projects.details.uninvoicedWorkDays', { days });
      }
      lines.push({
        category: uninvoiced.category,
        type: 'uninvoiced',
        index: 0,
        text,
      });
    }

    return lines
      .sort((left, right) => {
        const categoryDelta = (WARNING_CATEGORY_RANK[left.category] ?? 9)
          - (WARNING_CATEGORY_RANK[right.category] ?? 9);
        if (categoryDelta !== 0) return categoryDelta;
        const typeDelta = WARNING_TYPE_RANK[left.type] - WARNING_TYPE_RANK[right.type];
        if (typeDelta !== 0) return typeDelta;
        return left.index - right.index;
      })
      .map((line) => ({
        text: line.text,
        category: line.category,
        type: line.type,
      }));
  }, [warnings, t]);

  if (!project) return null;

  const hourlyRate = includeNonInvoicedWork
    ? project.effective_hourly_rate_with_non_invoiced_work
    : project.effective_hourly_rate;
  const profit = includeNonInvoicedWork
    ? project.contribution_with_non_invoiced_work
    : project.contribution;
  const margin = includeNonInvoicedWork
    ? project.margin_with_non_invoiced_work
    : project.margin;
  const profitPerHour = includeNonInvoicedWork
    ? project.contribution_per_hour_with_non_invoiced_work
    : project.contribution_per_hour;
  const revenueMetric = plannedVsActual.revenue || {};
  const revenueActual = includeNonInvoicedWork
    ? revenueMetric.actual_with_non_invoiced_work
    : revenueMetric.actual;
  const revenuePercentUsed = includeNonInvoicedWork
    ? revenueMetric.percent_used_with_non_invoiced_work
    : revenueMetric.percent_used;
  const profitActual = includeNonInvoicedWork
    ? plannedVsActual.profit?.actual_with_non_invoiced_work
    : plannedVsActual.profit?.actual;
  const marginActual = includeNonInvoicedWork
    ? plannedVsActual.margin_with_non_invoiced_work
    : project.margin;
  const hourRateActual = includeNonInvoicedWork
    ? plannedVsActual.hour_rate?.actual_with_non_invoiced_work
    : plannedVsActual.hour_rate?.actual;
  const costMetrics = COST_METRICS.map((key) => plannedVsActual[key]);
  const totalCostsPlanned = sumPresent(costMetrics.map((metric) => metric?.planned));
  const totalCostsActual = sumPresent(costMetrics.map((metric) => metric?.actual));
  const totalCostsPercentUsed = totalCostsPlanned && totalCostsActual != null
    ? totalCostsActual / totalCostsPlanned
    : null;

  return (
    <div className="pd-content">
      <ProjectStatusHeader
        color={statusColor}
        category={performanceCategory}
        summary={statusSummary}
        warnings={warningLines}
        startDate={formatDate(project.start_date)}
        endDate={formatDate(project.end_date)}
      />

      <div className="pd-card pd-family-card">
        <div className="pd-section-header">{t('projects.details.sectionProjectStructure')}</div>
        {members.length >= 2 && (
          <table className="pd-family-table">
            <thead>
              <tr>
                <th className="pd-family-check" />
                <th className="pd-label">{t('projects.columns.sourceProjectNumber')}</th>
                <th className="pd-label">{t('projects.columns.name')}</th>
                <th className="pd-label">{t('projects.columns.profit')}</th>
                <th className="pd-label">{t('projects.columns.remainingContractValue')}</th>
                <th className="pd-label">{t('projects.columns.contractValue')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="pd-family-check">
                  <input
                    type="checkbox"
                    checked={selectedProjectIds === null}
                    aria-label={t('projects.details.familyAll')}
                    onChange={onSelectFamily}
                  />
                </td>
                <td />
                <td className="pd-family-name">{t('projects.details.familyAll')}</td>
                <td />
                <td />
                <td />
              </tr>
              {indentedMembers.map(({ member, depth }) => {
                const profit = includeNonInvoicedWork
                  ? member.contribution_with_non_invoiced_work
                  : member.contribution;
                return (
                  <tr key={member.id}>
                    <td className="pd-family-check">
                      <input
                        type="checkbox"
                        checked={selectedProjectIds?.includes(member.id) ?? false}
                        aria-label={member.project_name || t('projects.columns.name')}
                        onChange={() => onToggleMember?.(member.id)}
                      />
                    </td>
                    <td>{member.source_system_project_number || '-'}</td>
                    <td>
                      <span
                        className="pd-family-name"
                        style={{ paddingLeft: depth * 16 }}
                      >
                        {member.project_name || '-'}
                      </span>
                    </td>
                    <td className="pd-value">{formatCurrency(profit)}</td>
                    <td className="pd-value">{formatCurrency(member.remaining_contract_value)}</td>
                    <td className="pd-value">{formatCurrency(member.contract_value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="pd-card">
        <div className="pd-section-header">{t('projects.details.sectionContract')}</div>
        <div className="pd-contract-top-row">
          <div className="pd-field">
            <span className="pd-label">{t('projects.columns.contractValue')}</span>
            <span className="pd-value">{formatCurrency(project.contract_value)}</span>
          </div>
          <div className="pd-field">
            <span className="pd-label">{t('projects.columns.remainingContractValue')}</span>
            <span className="pd-value">{formatCurrency(project.remaining_contract_value)}</span>
          </div>
          <Tippy content={t('projects.details.nonInvoicedWorkExplanation')} placement="top" interactive={false} delay={[200, 0]} maxWidth={300}>
            <div className="pd-field">
              <span className="pd-label">{t('projects.details.nonInvoicedWork')}</span>
              <span className="pd-value">{formatCurrency(project.non_invoiced_work)}</span>
            </div>
          </Tippy>
          <Tippy content={t('projects.details.creditedExplanation')} placement="top" interactive={false} delay={[200, 0]} maxWidth={300}>
            <div className="pd-field">
              <span className="pd-label">{t('projects.columns.credited')}</span>
              <span className="pd-value">{formatCurrency(project.credited)}</span>
            </div>
          </Tippy>
        </div>
        <ContractProgressBar
          contractValue={project.contract_value}
          spentPercentage={project.contract_value_spent_percentage}
          overInvoiced={project.over_invoiced}
          invoicedAmount={project.revenue}
          formatCurrency={formatCurrency}
          formatPercent={formatDecimalAsPercent}
        />
      </div>

      <div className="cashflow-scenario-toggles">
        <button
          type="button"
          role="switch"
          aria-checked={includeNonInvoicedWork}
          title={t('projects.details.includeNonInvoicedWorkExplanation')}
          className={`cashflow-scenario-toggle ${includeNonInvoicedWork ? 'on' : ''}`}
          onClick={() => setIncludeNonInvoicedWork((prev) => !prev)}
        >
          <span className="cashflow-switch" aria-hidden="true">
            <span className="cashflow-switch-thumb" />
          </span>
          <span className="cashflow-scenario-toggle-label">
            {t('overview.includeNonInvoicedWork')}
          </span>
        </button>
        <span className="pd-toggle-hint">{t('projects.details.includeNonInvoicedWorkHint')}</span>
      </div>

      <div className="pd-card">
        <div className="pd-section-header">{t('projects.details.sectionHoursEfficiency')}</div>
        <div className="pd-stat-row-3">
          <Tippy content={t('projects.details.rateExplanation')} placement="top" interactive={false} delay={[200, 0]} maxWidth={300}>
            <div className="pd-field">
              <span className="pd-label">{t('projects.columns.rate')}</span>
              <span className="pd-value">{formatCurrency(hourlyRate)}</span>
            </div>
          </Tippy>
          <Tippy content={t('projects.details.billableRateExplanation')} placement="top" interactive={false} delay={[200, 0]} maxWidth={300}>
            <div className="pd-field">
              <span className="pd-label">{t('projects.columns.billableRate')}</span>
              <span className="pd-value">{formatDecimalAsPercent(project.billable_rate)}</span>
            </div>
          </Tippy>
          <Tippy content={t('projects.details.costPerHourExplanation')} placement="top" interactive={false} delay={[200, 0]} maxWidth={300}>
            <div className="pd-field">
              <span className="pd-label">{t('projects.details.costPerHour')}</span>
              <span className="pd-value">{formatCurrency(project.cost_per_hour)}</span>
            </div>
          </Tippy>
        </div>
      </div>

      <div className="pd-card">
        <div className="pd-section-header">{t('projects.details.sectionProfitability')}</div>
        <div className="pd-stat-row-3">
          <Tippy content={t('projects.details.profitExplanation')} placement="top" interactive={false} delay={[200, 0]} maxWidth={300}>
            <div className="pd-field">
              <span className="pd-label">{t('projects.columns.profit')}</span>
              <span className="pd-value">{formatCurrency(profit)}</span>
            </div>
          </Tippy>
          <Tippy content={t('projects.details.contributionMarginExplanation')} placement="top" interactive={false} delay={[200, 0]} maxWidth={300}>
            <div className="pd-field">
              <span className="pd-label">{t('projects.details.margin')}</span>
              <span className="pd-value">{formatDecimalAsPercent(margin)}</span>
            </div>
          </Tippy>
          <Tippy content={t('projects.details.contributionPerHourExplanation')} placement="top" interactive={false} delay={[200, 0]} maxWidth={300}>
            <div className="pd-field">
              <span className="pd-label">{t('projects.details.contributionPerHour')}</span>
              <span className="pd-value">{formatCurrency(profitPerHour)}</span>
            </div>
          </Tippy>
        </div>
      </div>

      <div className="pd-donut-row">
        <div className="pd-card">
          <ProjectBreakdownDonut
            title={t('projects.details.sectionHours')}
            slices={hoursSlices}
            formatValue={(value) => value.toFixed(1)}
          />
        </div>
        <div className="pd-card">
          <ProjectBreakdownDonut
            title={t('projects.details.sectionRevenue')}
            slices={revenueSlices}
            formatValue={formatCurrency}
          />
        </div>
        <div className="pd-card">
          <ProjectBreakdownDonut
            title={t('projects.details.sectionCosts')}
            slices={costSlices}
            formatValue={formatCurrency}
          />
        </div>
      </div>

      <div className="pd-page-heading">{t('projects.details.sectionPlannedVsActual')}</div>

      <div className="pd-card">
        {!hasBudgetLines && (
          <p className="pd-planned-prompt">
            {t('projects.details.plannedBudgetPrompt')}{' '}
            <a href="mailto:post@revetall.ai">post@revetall.ai</a>
          </p>
        )}
        <table className="pd-comparison-table">
          <thead>
            <tr>
              <th />
              <th className="pd-label">{t('projects.details.planned')}</th>
              <th className="pd-label">{t('projects.details.actual')}</th>
              <th className="pd-label">{t('projects.details.percentUsed')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="pd-label">{t('projects.details.sectionHours')}</td>
              <td className="pd-value">{formatHoursValue(plannedVsActual.hours?.planned)}</td>
              <td className="pd-value">{formatHoursValue(plannedVsActual.hours?.actual)}</td>
              <td className="pd-value">{formatPercentUsed(plannedVsActual.hours)}</td>
            </tr>
            <tr>
              <td className="pd-label">{t('projects.details.sectionRevenue')}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.revenue?.planned)}</td>
              <td className="pd-value">{formatCurrency(revenueActual)}</td>
              <td className="pd-value">{formatPercentUsed({ percent_used: revenuePercentUsed })}</td>
            </tr>
            <tr className="pd-comparison-group">
              <td className="pd-section-header" colSpan={4}>{t('projects.details.sectionCosts')}</td>
            </tr>
            <tr>
              <td className="pd-label">{t('projects.details.personnelCost')}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.personnel_costs?.planned)}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.personnel_costs?.actual)}</td>
              <td className="pd-value">{formatPercentUsed(plannedVsActual.personnel_costs)}</td>
            </tr>
            <tr>
              <td className="pd-label">{t('projects.details.materialCosts')}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.material_costs?.planned)}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.material_costs?.actual)}</td>
              <td className="pd-value">{formatPercentUsed(plannedVsActual.material_costs)}</td>
            </tr>
            <tr>
              <td className="pd-label">{t('projects.details.subcontractorCosts')}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.subcontractor_costs?.planned)}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.subcontractor_costs?.actual)}</td>
              <td className="pd-value">{formatPercentUsed(plannedVsActual.subcontractor_costs)}</td>
            </tr>
            <tr>
              <td className="pd-label">{t('projects.columns.otherCosts')}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.other_costs?.planned)}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.other_costs?.actual)}</td>
              <td className="pd-value">{formatPercentUsed(plannedVsActual.other_costs)}</td>
            </tr>
            <tr className="pd-comparison-total">
              <td className="pd-label">{t('projects.columns.totalCosts')}</td>
              <td className="pd-value">{formatCurrency(totalCostsPlanned)}</td>
              <td className="pd-value">{formatCurrency(totalCostsActual)}</td>
              <td className="pd-value">{formatPercentUsed({ percent_used: totalCostsPercentUsed })}</td>
            </tr>
            <tr className="pd-comparison-group pd-comparison-group-result">
              <td className="pd-section-header" colSpan={4}>{t('projects.details.sectionResult')}</td>
            </tr>
            <tr>
              <td className="pd-label">{t('projects.columns.profit')}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.profit?.planned)}</td>
              <td className="pd-value">{formatCurrency(profitActual)}</td>
              <td className="pd-value">{formatPercentUsed(plannedVsActual.profit)}</td>
            </tr>
            <tr>
              <td className="pd-label">{t('projects.details.margin')}</td>
              <td className="pd-value">{formatDecimalAsPercent(plannedVsActual.margin)}</td>
              <td className="pd-value">{formatDecimalAsPercent(marginActual)}</td>
              <td className="pd-value">{formatPercentUsed(null)}</td>
            </tr>
            <tr>
              <td className="pd-label">{t('projects.details.hourRate')}</td>
              <td className="pd-value">{formatCurrency(plannedVsActual.hour_rate?.planned)}</td>
              <td className="pd-value">{formatCurrency(hourRateActual)}</td>
              <td className="pd-value">{formatPercentUsed(plannedVsActual.hour_rate)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectDetailContent;
